<template>
    <div class="manage-documents-page">
        <div v-if="errorMessage" class="alert alert-danger" role="alert">
            {{ errorMessage }}
        </div>

        <div v-if="successMessage" class="alert alert-success" role="alert">
            {{ successMessage }}
        </div>

        <div class="manage-documents-main">
            <aside class="manage-documents-categories card">
                <div class="card-header py-2">
                    <div class="manage-documents-section-title mb-2">Categories</div>
                    <div class="input-group input-group-sm">
                        <input v-model="newCategoryName" placeholder="New category" class="form-control" />
                        <div class="input-group-append">
                            <button class="btn btn-secondary" @click="addCategory">Add</button>
                        </div>
                    </div>
                </div>

                <div class="card-body p-2 manage-documents-category-body">
                    <ul class="manage-documents-category-list">
                        <li @click="selectCategory(null)" :style="getCategoryStyle(null)">
                            All documents
                        </li>
                        <li v-for="category in categories" :key="category.Id" @click="selectCategory(category)" :style="getCategoryStyle(category)">
                            {{ category.Name }}
                        </li>
                    </ul>
                </div>
            </aside>

            <section class="manage-documents-files card">
                <div class="card-header py-2 d-flex align-items-center justify-content-between">
                    <div class="manage-documents-section-title mb-0">Documents</div>
                    <div class="d-flex align-items-center">
                        <small class="text-muted mr-2">{{ filteredDocuments.length }} items</small>
                    </div>
                </div>

                <div class="card-body p-0 manage-documents-table-wrap">
                    <table class="table table-sm mb-0">
                        <thead>
                            <tr>
                                <th>Title</th>
                                <th>File</th>
                                <th style="width:150px;">Category</th>
                                <th style="width:80px;">Public</th>
                                <th style="width:70px;">Order</th>
                                <th style="width:90px;">Archived</th>
                                <th style="width:180px;">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="doc in filteredDocuments" :key="doc.Id">
                                <td>{{ doc.Title }}</td>
                                <td>
                                    <div>{{ doc.FileName }}</div>
                                    <small class="text-muted" v-if="doc.ContentType">{{ doc.ContentType }}</small>
                                </td>
                                <td>{{ doc.CategoryName }}</td>
                                <td>{{ doc.IsPublic ? 'Yes' : 'No' }}</td>
                                <td>{{ doc.Order }}</td>
                                <td>{{ doc.IsArchived ? 'Yes' : 'No' }}</td>
                                <td>
                                    
                                    <button 
                                        class="btn btn-sm btn-outline-success mr-2" 
                                        @click="downloadDocument(doc)">
                                        <i class="fa fa-download"></i>
                                    </button>

                                    <button class="btn btn-sm btn-outline-primary mr-2" @click="startEdit(doc)">Edit</button>
                                    <button class="btn btn-sm btn-danger" @click="deleteDocument(doc.Id)">Delete</button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="card-footer py-2 d-flex justify-content-end">
                    <button class="btn btn-dark btn-sm" @click="openCreateDocument">Add Document</button>
                </div>
            </section>

            <aside class="manage-documents-editor">
                <div class="card mb-3" v-if="sidebarMode === 'create'">
                    <div class="card-header py-2">
                        <div class="manage-documents-section-title mb-0">Add Document</div>
                    </div>

                    <div class="card-body">
                        <div class="form-group mb-2">
                            <label class="mb-1">Title</label>
                            <input v-model="newTitle" class="form-control form-control-sm" placeholder="Document title" />
                        </div>

                        <div class="form-group mb-2">
                            <label class="mb-1">Category</label>
                            <select v-model="newCategoryId" class="form-control form-control-sm">
                                <option disabled value="">Select a category</option>
                                <option v-for="category in categories" :key="category.Id" :value="category.Id">
                                    {{ category.Name }}
                                </option>
                            </select>
                        </div>

                        <div class="d-flex gap-2 mb-2">
                            <div class="form-group flex-fill mb-0 mr-2">
                                <label class="mb-1">Order</label>
                                <input v-model.number="newOrder" type="number" class="form-control form-control-sm" />
                            </div>
                            <div class="form-group flex-fill mb-0">
                                <label class="mb-1 d-block">Visibility</label>
                                <label class="manage-documents-check mr-3">
                                    <input type="checkbox" v-model="newIsPublic" /> Public
                                </label>
                                <label class="manage-documents-check">
                                    <input type="checkbox" v-model="newIsArchived" /> Archived
                                </label>
                            </div>
                        </div>

                        <div class="form-group mb-2">
                            <label class="mb-1 d-block">File</label>
                            <input type="file" ref="createFile" class="form-control-file" @change="handleCreateFileChange" />
                            <small class="text-muted" v-if="newFileName">Selected: {{ newFileName }}</small>
                        </div>

                        <div class="form-group mb-0">
                            <label class="mb-1">New category</label>
                            <div class="input-group input-group-sm">
                                <input v-model="newCategoryName" placeholder="Add category" class="form-control" />
                                <div class="input-group-append">
                                    <button class="btn btn-secondary" @click="addCategory">Add</button>
                                </div>
                            </div>
                        </div>

                        <button class="btn btn-dark btn-sm btn-block mt-3" @click="addDocument">Create document</button>
                    </div>
                </div>

                <div class="card" v-else>
                    <div class="card-header py-2 d-flex align-items-center justify-content-between">
                        <div class="manage-documents-section-title mb-0">Edit Document</div>
                        <button v-if="editingDocument" class="btn btn-link btn-sm p-0" @click="cancelEdit">Cancel</button>
                    </div>

                    <div class="card-body" v-if="editingDocument">
                        <div class="form-group mb-2">
                            <label class="mb-1">Title</label>
                            <input v-model="editingDocument.Title" class="form-control form-control-sm" />
                        </div>

                        <div class="form-group mb-2">
                            <label class="mb-1">Category</label>
                            <select v-model="editingDocument.CategoryId" class="form-control form-control-sm">
                                <option v-for="category in categories" :key="category.Id" :value="category.Id">
                                    {{ category.Name }}
                                </option>
                            </select>
                        </div>

                        <div class="d-flex gap-2 mb-2">
                            <div class="form-group flex-fill mb-0 mr-2">
                                <label class="mb-1">Order</label>
                                <input v-model.number="editingDocument.Order" type="number" class="form-control form-control-sm" />
                            </div>
                            <div class="form-group flex-fill mb-0">
                                <label class="mb-1 d-block">Visibility</label>
                                <label class="manage-documents-check mr-3">
                                    <input type="checkbox" v-model="editingDocument.IsPublic" /> Public
                                </label>
                                <label class="manage-documents-check">
                                    <input type="checkbox" v-model="editingDocument.IsArchived" /> Archived
                                </label>
                            </div>
                        </div>

                        <button class="btn btn-primary btn-sm btn-block" @click="saveDocument">Save changes</button>
                    </div>

                    <div class="card-body text-muted" v-else>
                        Select a document to edit it, or click <strong>Add Document</strong> to switch to create mode.
                    </div>
                </div>
            </aside>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { GuidService } from '../../services/guid'

export default {
    data: function () {
        return {
            documents: [],
            categories: [],
            selectedCategoryId: null,
            sidebarMode: 'edit',
            newCategoryName: '',
            newTitle: '',
            newOrder: 0,
            newIsPublic: false,
            newIsArchived: false,
            newCategoryId: null,
            newFile: null,
            newFileName: '',
            editingDocument: null,
            errorMessage: '',
            successMessage: ''
        }
    },

    computed: {
        rootUrl: function () {
            if (window.location.origin === 'http://localhost:3000') {
                return 'http://localhost:62737'
            }

            return '.'
        },

        filteredDocuments: function () {
            if (!this.selectedCategoryId) {
                return this.documents
            }

            return this.documents.filter(function (doc) {
                return doc.CategoryId === this.selectedCategoryId
            }, this)
        }
    },

    created: function () {
        this.refreshData()
    },

    methods: {
        refreshData: function () {
            this.fetchCategories()
            this.fetchDocuments()
        },
        downloadDocument: function (doc) {
            axios.get(this.rootUrl + '/api/files/' + doc.Id, {
                responseType: 'blob'
            })
            .then((res) => {
                const contentType = res.headers['content-type'] || 'application/octet-stream';

                const blob = new Blob([res.data], { type: contentType });

                let fileName = doc.FileName;

                const disposition = res.headers['content-disposition'];
                if (disposition && disposition.includes('filename=')) {
                    fileName = disposition
                        .split('filename=')[1]
                        .replace(/"/g, '');
                }

                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = fileName;

                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
            });

        },

        clearMessages: function () {
            this.errorMessage = ''
            this.successMessage = ''
        },

        fetchCategories: function () {
            var self = this

            return axios.get(this.rootUrl + '/api/files/categories')
                .then(function (res) {
                    self.categories = res.data || []
                })
                .catch(function () {
                    self.errorMessage = 'Failed to load categories'
                })
        },

        fetchDocuments: function () {
            var self = this

            return axios.get(this.rootUrl + '/api/hlsdocuments')
                .then(function (res) {
                    self.documents = res.data || []
                })
                .catch(function () {
                    self.errorMessage = 'Failed to load documents'
                })
        },

        selectCategory: function (category) {
            this.selectedCategoryId = category ? category.Id : null
        },

        getCategoryStyle: function (category) {
            var isSelected = category === null
                ? this.selectedCategoryId === null
                : this.selectedCategoryId === category.Id

            return {
                cursor: 'pointer',
                padding: '6px 8px',
                marginBottom: '4px',
                borderRadius: '4px',
                background: isSelected ? '#2C3D47' : 'transparent',
                color: isSelected ? '#fff' : '#212529'
            }
        },

        addCategory: function () {
            var name = (this.newCategoryName || '').trim()
            if (!name) {
                this.errorMessage = 'Category name is required'
                return
            }

            this.clearMessages()

            var self = this

            axios.post(this.rootUrl + '/api/files/categories', { name: name })
                .then(function (res) {
                    var category = res.data
                    self.newCategoryName = ''
                    self.newCategoryId = category.Id
                    self.successMessage = 'Category added'
                    return self.fetchCategories()
                })
                .catch(function () {
                    self.errorMessage = 'Failed to add category'
                })
        },

        handleCreateFileChange: function (event) {
            var file = event.target.files[0]
            this.newFile = file || null
            this.newFileName = file ? file.name : ''
        },

        addDocument: function () {
            if (!this.newFile) {
                this.errorMessage = 'Choose a file first';
                return;
            }

            if (!this.newCategoryId) {
                this.errorMessage = 'Category is required';
                return;
            }

            this.clearMessages()

            var guid = GuidService.new()
            var formData = new FormData()
            formData.append('file', this.newFile)
            formData.append('title', this.newTitle || this.newFile.name)
            formData.append('order', String(this.newOrder || 0))
            formData.append('isPublic', this.newIsPublic ? 'true' : 'false')
            formData.append('isArchived', this.newIsArchived ? 'true' : 'false')

            if (this.newCategoryId) {
                formData.append('categoryId', this.newCategoryId)
            }

            var self = this

            axios.post(this.rootUrl + '/api/hlsdocuments/add/' + guid, formData)
                .then(function () {
                    self.successMessage = 'Document added'
                    self.sidebarMode = 'edit'
                    self.newTitle = ''
                    self.newOrder = 0
                    self.newIsPublic = false
                    self.newIsArchived = false
                    self.newCategoryId = null
                    self.newFile = null
                    self.newFileName = ''
                    if (self.$refs.createFile) {
                        self.$refs.createFile.value = ''
                    }
                    self.fetchDocuments()
                })
                .catch(function () {
                    self.errorMessage = 'Failed to create document'
                })
        },

        startEdit: function (doc) {
            this.sidebarMode = 'edit'
            this.editingDocument = {
                Id: doc.Id,
                Title: doc.Title,
                CategoryId: doc.CategoryId,
                Order: doc.Order,
                IsPublic: doc.IsPublic,
                IsArchived: doc.IsArchived
            }
            this.clearMessages()
        },

        cancelEdit: function () {
            this.editingDocument = null
        },

        openCreateDocument: function () {
            this.sidebarMode = 'create'
            this.clearMessages()
        },

        saveDocument: function () {
            if (!this.editingDocument) return

            if (!this.editingDocument.Title || !this.editingDocument.Title.trim()) {
                this.errorMessage = 'Title is required'
                return
            }

            this.clearMessages()

            var self = this

            axios.put(this.rootUrl + '/api/hlsdocuments/' + this.editingDocument.Id, {
                title: this.editingDocument.Title,
                categoryId: this.editingDocument.CategoryId,
                order: this.editingDocument.Order,
                isPublic: this.editingDocument.IsPublic,
                isArchived: this.editingDocument.IsArchived
            })
                .then(function () {
                    self.successMessage = 'Document updated'
                    self.fetchDocuments()
                })
                .catch(function () {
                    self.errorMessage = 'Failed to save document'
                })
        },

        deleteDocument: function (id) {
            if (!confirm('Delete this document?')) return

            var self = this

            axios.delete(this.rootUrl + '/api/hlsdocuments/' + id)
                .then(function () {
                    self.successMessage = 'Deleted'
                    if (self.editingDocument && self.editingDocument.Id === id) {
                        self.editingDocument = null
                    }
                    self.fetchDocuments()
                })
                .catch(function () {
                    self.errorMessage = 'Delete failed'
                })
        }
    }
}
</script>

<style>
.manage-documents-page {
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding: 20px;
    box-sizing: border-box;
}

.manage-documents-main {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr) 360px;
    gap: 12px;
}

.manage-documents-categories,
.manage-documents-files,
.manage-documents-editor {
    min-height: 0;
}

.manage-documents-category-body {
    overflow: auto;
}

.manage-documents-table-wrap {
    overflow: auto;
}

.manage-documents-section-title {
    font-weight: 700;
}

.manage-documents-category-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.manage-documents-check {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    white-space: nowrap;
}

.manage-documents-files .table td,
.manage-documents-files .table th {
    vertical-align: middle;
}

.form-control-file {
    display: block;
    width: 100%;
}

@media (max-width: 1200px) {
    .manage-documents-main {
        grid-template-columns: 220px 1fr;
    }

    .manage-documents-editor {
        grid-column: 1 / -1;
    }
}

@media (max-width: 768px) {
    .manage-documents-main {
        grid-template-columns: 1fr;
    }
}
</style>
